import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Message } from '@volt/shared';
import { useI18n } from '../i18n/context.js';
import { copyToClipboard } from '../lib/dom.js';
import { hapticFeedback } from '../lib/haptics.js';
import { buildShareUrl } from '../lib/share.js';
import { buildShareTargets, shareExcerpt } from '../lib/shareCard.js';
import { useFocusTrap } from '../hooks/useFocusTrap.js';
import { useSession } from '../state/session.js';
import { getShareErrorKey } from './ShareModal.js';
import { Icon } from './Icon.js';
import type { IconName } from './Icon.js';

export interface ShareSheetProps {
  conversationId: string | null;
  message: Message | null;
  /** Open the full share dialog (send to Volt chats, select several messages). */
  onMore: () => void;
  onClose: () => void;
}

const STYLES = ['sunrise', 'meadow', 'night'] as const;

/**
 * Bottom sheet "Share answer": a swipeable preview card plus quick targets.
 * Nothing is published until the user picks a target — then a public link to the
 * conversation is created (or reused), exactly like the existing share dialog.
 */
export function ShareSheet({ conversationId, message, onMore, onClose }: ShareSheetProps) {
  const { t } = useI18n();
  const { api } = useSession();
  const open = conversationId !== null && message !== null;
  const trapRef = useFocusTrap(open, onClose);
  const [style, setStyle] = useState(0);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const urlRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setStyle(0);
    setBusy(false);
    setCopied(false);
    setError(null);
    urlRef.current = null;
    if (trackRef.current) trackRef.current.scrollLeft = 0;
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [conversationId, message?.id]);

  if (!open || !message || !conversationId) return null;
  const excerpt = shareExcerpt(message.content);

  async function ensureUrl(): Promise<string | null> {
    if (urlRef.current) return urlRef.current;
    setBusy(true);
    setError(null);
    try {
      const existing = await api.getShare(conversationId!).then((r) => r.snapshot).catch(() => null);
      const snapshot = existing ?? (await api.createShare(conversationId!)).snapshot;
      urlRef.current = buildShareUrl(window.location.origin, snapshot.token);
      return urlRef.current;
    } catch (err) {
      setError(t(getShareErrorKey(err)));
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function run(action: (url: string) => void | Promise<void>) {
    if (busy) return;
    const url = await ensureUrl();
    if (url) {
      hapticFeedback('tap');
      await action(url);
    }
  }

  const openLink = (href: string) => { window.open(href, '_blank', 'noopener'); };
  const actions: { key: string; label: string; icon: IconName; tone: string; run: () => void }[] = [
    {
      key: 'copy', label: copied ? t('share.copied') : t('share.copyLink'), icon: copied ? 'check' : 'copy', tone: 'neutral',
      run: () => void run(async (url) => {
        if (await copyToClipboard(url)) {
          setCopied(true);
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setCopied(false), 1500);
        }
      }),
    },
    { key: 'telegram', label: 'Telegram', icon: 'send', tone: 'telegram', run: () => void run((url) => openLink(buildShareTargets(url, excerpt).telegram)) },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'chat', tone: 'whatsapp', run: () => void run((url) => openLink(buildShareTargets(url, excerpt).whatsapp)) },
    { key: 'sms', label: t('shareSheet.messages'), icon: 'chat', tone: 'messages', run: () => void run((url) => { window.location.href = buildShareTargets(url, excerpt).sms; }) },
    {
      key: 'more', label: t('shareSheet.more'), icon: 'more', tone: 'neutral',
      run: () => void run(async (url) => {
        if (typeof navigator.share === 'function') {
          try { await navigator.share({ title: t('app.name'), text: excerpt, url }); } catch { /* dismissed */ }
        } else if (await copyToClipboard(url)) {
          setCopied(true);
        }
      }),
    },
  ];

  return createPortal(
    <div className="share-sheet-overlay" onPointerDown={onClose}>
      <div
        ref={trapRef}
        className="share-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('shareSheet.title')}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <div className="share-sheet-grabber" aria-hidden="true" />
        <h2 className="share-sheet-title">{t('shareSheet.title')}</h2>
        <p className="share-sheet-hint">{t('shareSheet.hint')}</p>

        <div
          className="share-sheet-track"
          ref={trackRef}
          onScroll={(event) => {
            const el = event.currentTarget;
            const next = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
            if (next !== style) setStyle(Math.min(STYLES.length - 1, Math.max(0, next)));
          }}
        >
          {STYLES.map((name) => (
            <div key={name} className={`share-card-bg share-card-bg-${name}`}>
              <div className="share-card">
                <p className="share-card-text">{excerpt}</p>
                <div className="share-card-footer">
                  <strong>{t('app.name')}</strong>
                  <Icon name="bot" size={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="share-sheet-dots" aria-hidden="true">
          {STYLES.map((name, i) => <i key={name} className={i === style ? 'is-active' : ''} />)}
        </div>

        {error ? <p className="bubble-error share-sheet-error">{error}</p> : null}
        <div className={`share-sheet-actions${busy ? ' is-busy' : ''}`}>
          {actions.map((action) => (
            <button key={action.key} type="button" className="share-action" disabled={busy} onClick={action.run}>
              <span className={`share-action-icon share-action-${action.tone}`}>
                {busy && action.key === 'copy' ? <span className="spinner spinner-sm" /> : <Icon name={action.icon} size={24} />}
              </span>
              <span className="share-action-label">{action.label}</span>
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-ghost share-sheet-chats" onClick={onMore}>
          {t('shareSheet.toChats')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
